import { Navigate } from 'react-router-dom'
import { useAuth } from '../../shared/contexts/AuthContext'
import { AccessDenied, LoadingState } from '../common'
import { canCreateCurriculum, canManage } from '../../lib/utils/roleUtils'
import { debug } from '../../lib/utils/debug'

interface ProtectedRouteProps {
  children: React.ReactNode
  requireAdmin?: boolean
  requireSuperAdmin?: boolean
  /** Manager+ (curriculum create/edit) */
  requireManager?: boolean
}

export function ProtectedRoute({
  children,
  requireAdmin = false,
  requireSuperAdmin = false,
  requireManager = false,
}: ProtectedRouteProps) {
  const { user, loading } = useAuth()

  debug.log('🛡️ ProtectedRoute:', {
    user: user?.id,
    loading,
    requireAdmin,
    requireSuperAdmin,
    requireManager,
  })

  if (loading) {
    debug.log('⏳ ProtectedRoute: Loading...')
    return <LoadingState message="Checking access..." fullScreen />
  }

  if (!user) {
    debug.log('🚫 ProtectedRoute: No user, redirecting to auth')
    return <Navigate to="/auth" replace />
  }

  debug.log('✅ ProtectedRoute: User authenticated, checking permissions')

  if (requireSuperAdmin && user.role !== 'super_admin') {
    debug.log('🚫 ProtectedRoute: Super admin required but user role is:', user.role)
    return (
      <AccessDenied
        title="Super Admin Access Required"
        message="You need super admin permissions to access this page."
        icon="shield"
      />
    )
  }

  if (requireAdmin && user.role !== 'admin' && user.role !== 'super_admin') {
    debug.log('🚫 ProtectedRoute: Admin required but user role is:', user.role)
    return (
      <AccessDenied
        title="Admin Access Required"
        message="You need admin permissions to access this page."
        icon="lock"
      />
    )
  }

  if (
    requireManager &&
    !(canManage(user.role) || canCreateCurriculum(user.role))
  ) {
    debug.log('🚫 ProtectedRoute: Manager required but user role is:', user.role)
    return (
      <AccessDenied
        title="Leader Access Required"
        message="You need manager or admin permissions to edit curriculum."
        icon="lock"
      />
    )
  }

  debug.log('✅ ProtectedRoute: Access granted')
  return <>{children}</>
}
